import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { MediaLenient } from "@/components/forms/schemas/media";
import { ENRICHMENT_STATUSES } from "@/lib/enrichment";

const disabled = fieldConfig({ inputProps: { disabled: true } });

/**
 * Edit schema for a Person (cast/crew of movies, plays and performances), bound to `PersonDbDto`
 * from `GET /api/people/{id}` and mapped onto `UpdatePersonCommand` by
 * `usePeopleRepository.update`.
 *
 * A new name or first photo is written through to every cast list carrying the person, by the
 * backend. `popularity` and `followers` come from TMDB and the app, so they are shown, not
 * edited; `metadata.source`/`tmdbId` are the TMDB link and stay read-only too.
 *
 * `slug` is nullish and must NEVER reach the API as `""` — see `update`.
 */
export const personEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  name: z.string().min(1, "Required"),
  // `UpdatePersonCommandValidator`: `MustBeValidSlug` — lowercase, digits, single hyphens.
  slug: z.coerce
    .string()
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Lowercase letters, digits and single hyphens, e.g. robert-altman",
    )
    .nullish(),
  aliases: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  knownForDepartment: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Known for department",
        inputProps: { placeholder: "e.g. Acting, Directing, Writing" },
      }),
    ),
  about: z
    .string()
    .nullish()
    .superRefine(fieldConfig({ fieldType: "textarea" })),
  /**
   * `translations.<locale>.name` — the person's name as written in each language, edited as
   * rows (`toTranslationRows` on load, `toTranslationsMap` on save). Any locale code is allowed.
   * Tina fills a blank locale from the source page it found the person on.
   */
  translations: z
    .array(
      z.object({
        locale: z
          .string()
          .min(2, "A language code, e.g. el")
          .superRefine(
            fieldConfig({
              label: "Language",
              inputProps: { placeholder: "el" },
            }),
          ),
        name: z.string().min(1, "Required"),
      }),
    )
    .nullish()
    .default([])
    .superRefine(fieldConfig({ label: "Name per language" })),
  artistId: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({ label: "Linked artist", fieldType: "artistRef" }),
    ),
  status: z.enum(["Draft", "Complete", "Archived"]),
  hide: z.boolean().default(false),
  media: MediaLenient.superRefine(
    fieldConfig({ fieldType: "media", customData: { collection: "People" } }),
  ),
  metadata: z
    .object({
      source: z.string().nullish().superRefine(disabled),
      tmdbId: z.coerce.number().nullish().superRefine(disabled),
      imdbId: z.string().nullish(),
    })
    .nullish(),
  popularity: z.coerce.number().nullish().superRefine(disabled),
  followers: z.coerce.number().nullish().superRefine(disabled),
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),
  createdBy: z.string().nullish().superRefine(disabled),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
  updatedBy: z.string().nullish().superRefine(disabled),
  updatedAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
});

export type PersonEditFormType = z.infer<typeof personEditSchema>;

/** The `UpdatePersonCommand` members the form may send. Everything else is display-only. */
export const PERSON_UPDATE_KEYS = [
  "name",
  "slug",
  "aliases",
  "knownForDepartment",
  "about",
  "translations",
  "artistId",
  "status",
  "hide",
  "media",
  "metadata",
] as const;
